import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../design/Icon';
import { Spinner } from '../components/shared/States';
import { HelpArticleBody } from './HelpArticle';
import { useHelp } from './HelpContext';
import { articlesForPath, searchHelp } from './helpData';

const START_HERE = ['welcome', 'find-your-way-around', 'start-supervisors', 'start-managers', 'start-admins'];

function ArticleLink({ article, onOpen, snippet }) {
  return (
    <li>
      <button type="button" className="help-link-row" onClick={() => onOpen(article.id)}>
        <span className="help-link-title">{article.title}</span>
        <span className="help-link-sub">{snippet || article.summary}</span>
      </button>
    </li>
  );
}

/** The Help side panel: help for the current page, search, topics, and articles, without leaving the page. */
export function HelpPanel() {
  const { open, closeHelp, help, error, ensureLoaded, articleId, setArticleId } = useHelp();
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState(null);
  const [history, setHistory] = useState([]);
  const searchRef = useRef(null);
  const bodyRef = useRef(null);
  const headingRef = useRef(null);
  const returnFocus = useRef(null);

  useEffect(() => {
    if (open) {
      returnFocus.current = document.activeElement;
      setTimeout(() => (articleId ? headingRef.current : searchRef.current)?.focus(), 0);
    } else {
      setQuery('');
      setCategoryId(null);
      setHistory([]);
      if (returnFocus.current && typeof returnFocus.current.focus === 'function') returnFocus.current.focus();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !e.defaultPrevented && !document.querySelector('[aria-modal="true"]')) closeHelp();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closeHelp]);

  useEffect(() => {
    bodyRef.current?.scrollTo?.(0, 0);
  }, [articleId, categoryId, query]);

  const pageArticles = useMemo(() => articlesForPath(help, location.pathname).slice(0, 5), [help, location.pathname]);
  const results = useMemo(() => searchHelp(help, query, 12), [help, query]);
  const article = help && articleId ? help.byId.get(articleId) : null;

  if (!open) return null;

  const openArticle = (id, anchor) => {
    setHistory((h) => [...h, { articleId, categoryId, query }]);
    setArticleId(id);
    setQuery('');
    if (anchor) setTimeout(() => bodyRef.current?.querySelector(`[id="${CSS.escape(anchor)}"]`)?.scrollIntoView({ block: 'start' }), 50);
  };
  const back = () => {
    const previous = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setArticleId(previous ? previous.articleId : null);
    setCategoryId(previous ? previous.categoryId : null);
    setQuery(previous ? previous.query : '');
  };
  const onLink = (node) => {
    if (node.kind === 'app') {
      navigate(node.href);
      return;
    }
    openArticle(node.href, node.anchor);
  };
  const openCenter = () => {
    closeHelp();
    navigate(article ? `/help/${article.id}` : '/help');
  };

  const canGoBack = Boolean(article || categoryId || history.length);
  const category = help && categoryId ? help.categories.find((c) => c.id === categoryId) : null;

  let content;
  if (error && !help) {
    content = (
      <div className="help-empty">
        <p>Help couldn't load. Check your connection and try again.</p>
        <button type="button" className="btn" onClick={ensureLoaded}>
          Try again
        </button>
      </div>
    );
  } else if (!help) {
    content = <Spinner label="Loading help…" />;
  } else if (article) {
    const related = article.related.map((id) => help.byId.get(id)).filter(Boolean);
    content = (
      <article className="help-article help-article-panel" aria-labelledby="help-panel-article-title">
        <h2 id="help-panel-article-title" className="help-article-title" tabIndex={-1} ref={headingRef}>
          {article.title}
        </h2>
        <HelpArticleBody article={article} onLink={onLink} />
        {related.length > 0 && (
          <section className="help-related" aria-label="Related articles">
            <h3>Related articles</h3>
            <ul className="help-link-list">
              {related.map((r) => (
                <ArticleLink key={r.id} article={r} onOpen={openArticle} />
              ))}
            </ul>
          </section>
        )}
      </article>
    );
  } else if (query.trim()) {
    content = (
      <section aria-live="polite">
        <h3 className="help-section-title">
          {results.length ? `${results.length} result${results.length === 1 ? '' : 's'}` : 'No results'}
        </h3>
        {results.length ? (
          <ul className="help-link-list">
            {results.map((r) => (
              <ArticleLink key={r.article.id} article={r.article} snippet={r.snippet} onOpen={openArticle} />
            ))}
          </ul>
        ) : (
          <p className="help-muted">Try different words, or browse the topics below.</p>
        )}
      </section>
    );
  } else if (category) {
    content = (
      <section>
        <h3 className="help-section-title">{category.title}</h3>
        <ul className="help-link-list">
          {(help.byCategory.get(category.id) || []).map((a) => (
            <ArticleLink key={a.id} article={a} onOpen={openArticle} />
          ))}
        </ul>
      </section>
    );
  } else {
    const start = START_HERE.map((id) => help.byId.get(id)).filter(Boolean);
    content = (
      <>
        {pageArticles.length > 0 && (
          <section>
            <h3 className="help-section-title">Help for this page</h3>
            <ul className="help-link-list">
              {pageArticles.map((a) => (
                <ArticleLink key={a.id} article={a} onOpen={openArticle} />
              ))}
            </ul>
          </section>
        )}
        <section>
          <h3 className="help-section-title">Browse topics</h3>
          <ul className="help-topic-list">
            {help.categories.map((c) => (
              <li key={c.id}>
                <button type="button" className="help-topic-row" onClick={() => setCategoryId(c.id)}>
                  <Icon name={c.icon} />
                  <span>
                    <span className="help-link-title">{c.title}</span>
                    <span className="help-link-sub">{c.description}</span>
                  </span>
                  <Icon name="chevR" className="ico-sm help-topic-chev" />
                </button>
              </li>
            ))}
          </ul>
        </section>
        {start.length > 0 && (
          <section>
            <h3 className="help-section-title">Start here</h3>
            <ul className="help-link-list">
              {start.map((a) => (
                <ArticleLink key={a.id} article={a} onOpen={openArticle} />
              ))}
            </ul>
          </section>
        )}
      </>
    );
  }

  return (
    <aside className="help-panel" role="dialog" aria-modal="false" aria-label="Help">
      <div className="help-panel-head">
        {canGoBack ? (
          <button type="button" className="btn btn-ghost icon-btn" aria-label="Back" onClick={back}>
            <Icon name="chevL" />
          </button>
        ) : (
          <Icon name="help" className="help-panel-mark" />
        )}
        <h2 className="section-title grow">Help</h2>
        <button type="button" className="btn btn-ghost icon-btn" aria-label="Open in Help Center" title="Open in Help Center" onClick={openCenter}>
          <Icon name="external" />
        </button>
        <button type="button" className="btn btn-ghost icon-btn" aria-label="Close help" onClick={closeHelp}>
          <Icon name="x" />
        </button>
      </div>
      <div className="help-panel-search">
        <label className="help-search">
          <span className="sr-only">Search help</span>
          <Icon name="search" />
          <input
            ref={searchRef}
            type="search"
            placeholder="Search help"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (articleId) {
                setHistory((h) => [...h, { articleId, categoryId, query: '' }]);
                setArticleId(null);
              }
            }}
          />
        </label>
      </div>
      <div className="help-panel-body" ref={bodyRef}>
        {content}
      </div>
      <div className="help-panel-foot">
        <span className="help-muted">
          Press <kbd>?</kbd> to open or close help
        </span>
        <button type="button" className="btn btn-ghost" onClick={openCenter}>
          Help Center
        </button>
      </div>
    </aside>
  );
}
